import { ArrowIcon, ButtonLink } from '@/components/ui/Button';
import { ErrorState, Skeleton } from '@/components/ui/Feedback';
import { SectionIntro } from '@/components/ui/SectionIntro';
import { DIVISION_PATH, useCategories, useProducts } from '@/features/catalog/api';
import { ProductIndex } from '@/features/catalog/ProductIndex';
import { useSite } from '@/features/site/SiteProvider';
import { cn } from '@/lib/utils';
import { CategoryTile } from './CategoryTile';

/** Section B — apparel manufacturing: category bento grid + featured products from the live catalogue. */
export function ApparelShowcase() {
  const intro = useSite().content.catalogIntro.apparel;
  const categories = useCategories('APPAREL');
  const featured = useProducts({ division: 'APPAREL', featured: true, pageSize: 3 });

  return (
    <section aria-labelledby="apparel-title" className="section-y bg-surface">
      <div className="container-page">
        <div className="flex flex-col gap-6 lg:flex-row lg:items-end lg:justify-between">
          <SectionIntro id="apparel-title" eyebrow={intro.eyebrow} title={intro.heading} intro={intro.intro} />
          <ButtonLink to={DIVISION_PATH.APPAREL} variant="outline" icon={<ArrowIcon />} className="self-start lg:self-auto">
            View apparel catalogue
          </ButtonLink>
        </div>

        <div className="mt-12">
          {categories.isPending ? (
            <div className="grid auto-rows-[220px] grid-cols-2 gap-4 lg:auto-rows-[250px] lg:grid-cols-4">
              <Skeleton className="col-span-2 row-span-2 rounded-[8px]" />
              {[1, 2, 3, 4].map((i) => (
                <Skeleton key={i} className="rounded-[8px]" />
              ))}
            </div>
          ) : categories.isError ? (
            <ErrorState error={categories.error} onRetry={() => void categories.refetch()} />
          ) : (
            <div className="grid auto-rows-[220px] grid-cols-2 gap-4 lg:auto-rows-[250px] lg:grid-cols-4">
              {categories.data.map((c, i) => (
                <CategoryTile key={c.slug} category={c} featured={i === 0} className={cn(i === 0 && 'col-span-2 row-span-2', i > 0 && 'col-span-2 sm:col-span-1')} />
              ))}
            </div>
          )}
        </div>

        {featured.data && featured.data.data.length > 0 && (
          <div className="mt-20">
            <div className="flex items-end justify-between gap-6 pb-5">
              <h3 className="font-display text-[22px] font-bold tracking-tight text-primary">Featured apparel</h3>
              <ButtonLink to={DIVISION_PATH.APPAREL} variant="ghost" size="sm" icon={<ArrowIcon />}>
                All apparel
              </ButtonLink>
            </div>
            <div className="mt-2">
              <ProductIndex products={featured.data.data} />
            </div>
          </div>
        )}
      </div>
    </section>
  );
}
